import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Battery, Key, Smartphone, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { TECH_FEATURES } from '../../data/techFeatures';

const iconMap: Record<string, React.ReactNode> = {
  hpb: <Battery className="w-5 h-5" />,
  aer: <Zap className="w-5 h-5" />,
  app: <Smartphone className="w-5 h-5" />,
  nfc: <Key className="w-5 h-5" />
};

const cardTones = ['bg-white', 'bg-[#eef3ed]', 'bg-[#f7f0e5]', 'bg-[#f4f4f1]'];

export function TechInnovation() {
  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-12 right-[8%] h-64 w-64 rounded-full bg-[#ece1cf]/45 blur-3xl" />
        <div className="absolute bottom-0 left-[8%] h-60 w-60 rounded-full bg-[#dde8d9]/45 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.15fr] gap-8 items-start mb-12">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="home-kicker"
            >
              Innovacion Zwheel
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="home-title mb-5"
            >
              Tecnologia pensada para hacer cada trayecto mas comodo
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-copy"
            >
              Mas autonomia, frenada regenerativa, app y acceso inteligente para que disfrutes de una conduccion mas practica, segura y conectada.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.14 }}
            className="rounded-[2rem] border border-black/5 bg-[var(--zw-ivory)] p-7 shadow-[0_18px_60px_rgba(0,0,0,0.05)]"
          >
            <div className="text-sm uppercase tracking-[0.18em] text-gray-500 mb-3">Ventajas reales</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Innovacion que se nota desde el primer trayecto</h3>
            <p className="text-gray-600 leading-relaxed mb-5">
              Potencia, control, autonomia y conectividad para que moverte a diario resulte mas facil, mas fluido y mas fiable.
            </p>
            <Button asChild className="bg-black text-white hover:bg-gray-900">
              <Link to="/technology">
                Explorar tecnologia
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {TECH_FEATURES.map((tech, index) => (
            <motion.article
              key={tech.id}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: index * 0.07 }}
              className={`rounded-[1.9rem] border border-black/5 p-7 shadow-[0_16px_50px_rgba(0,0,0,0.04)] ${cardTones[index % cardTones.length]}`}
            >
              <div className="inline-flex items-center justify-center rounded-2xl bg-black text-white p-4 mb-6">
                {iconMap[tech.id]}
              </div>

              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
                {tech.title}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tech.name}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">{tech.description}</p>

              <div className="space-y-3 pt-5 border-t border-black/8">
                {tech.benefits.slice(0, 3).map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[var(--zw-moss)] mt-2 shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{benefit.title}</div>
                      <div className="text-xs text-gray-500 leading-relaxed">{benefit.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
